import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import PerformanceMetricCard from './components/PerformanceMetricCard';
import PerformanceTrendChart from './components/PerformanceTrendChart';
import RiskMatrixPanel from './components/RiskMatrixPanel';
import CAPAActionsTable from './components/CAPAActionsTable';
import RiskPredictionPanel from './components/RiskPredictionPanel';
import Icon from '../../components/AppIcon';
import { Helmet } from 'react-helmet';
import { risksService, capaService, metricsService, partnerService } from '../../services/partnerService';

const GovernancePerformanceDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [risks, setRisks] = useState([]);
  const [capaActions, setCAPAActions] = useState([]);
  const [metricsHistory, setMetricsHistory] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [risksData, capaData, metricsData, partnersData] = await Promise.all([
        risksService?.getAll(),
        capaService?.getAll(),
        metricsService?.getAll ? metricsService?.getAll() : metricsService?.getByPartner(undefined),
        partnerService?.getAll()
      ]);
      setRisks(risksData || []);
      setCAPAActions(capaData || []);
      setMetricsHistory(metricsData || []);
      setPartners(partnersData || []);
    } catch (err) {
      console.error('Error loading governance data:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  // ─── Global Rollup Metrics ────────────────────────────────────────────────
  const totalPartners = partners?.length || 0;
  const activePartners = partners?.filter(p => p?.status === 'approved')?.length || 0;
  const openRisks = risks?.filter(r => r?.risk_status === 'open')?.length || 0;
  const criticalRisks = risks?.filter(r => r?.severity === 'critical')?.length || 0;
  const openCAPAs = capaActions?.filter(c => c?.capa_status === 'open')?.length || 0;
  const overdueCAPAs = capaActions?.filter(c => {
    if (!c?.due_date || c?.capa_status === 'closed') return false;
    return new Date(c?.due_date) < new Date();
  })?.length || 0;

  // Average metrics across all partners
  const latestMetrics = metricsHistory?.[metricsHistory?.length - 1];
  const prevMetrics = metricsHistory?.[metricsHistory?.length - 2];

  const rollupMetrics = [
    {
      title: "Total Partners",
      value: String(totalPartners),
      unit: "",
      trend: "up",
      trendValue: `${activePartners} active`,
      benchmark: String(activePartners),
      icon: "Users",
      color: "primary"
    },
    {
      title: "Avg Win Rate",
      value: latestMetrics ? String(latestMetrics?.win_rate) : "0",
      unit: "%",
      trend: "up",
      trendValue: "+5.3%",
      benchmark: "65%",
      icon: "Target",
      color: "success"
    },
    {
      title: "Avg SLA Compliance",
      value: latestMetrics ? String(latestMetrics?.sla_compliance) : "0",
      unit: "%",
      trend: latestMetrics && prevMetrics ? (latestMetrics?.sla_compliance >= prevMetrics?.sla_compliance ? "up" : "down") : "up",
      trendValue: "-2.1%",
      benchmark: "95%",
      icon: "CheckCircle2",
      color: "error"
    },
    {
      title: "Open Risks",
      value: String(openRisks),
      unit: "",
      trend: criticalRisks > 0 ? "down" : "up",
      trendValue: `${criticalRisks} critical`,
      benchmark: "0",
      icon: "Shield",
      color: "warning"
    },
    {
      title: "Open CAPAs",
      value: String(openCAPAs),
      unit: "",
      trend: overdueCAPAs > 0 ? "down" : "up",
      trendValue: `${overdueCAPAs} overdue`,
      benchmark: "0",
      icon: "ListChecks",
      color: "error"
    }
  ];

  // Risk breakdown by category
  const riskByCategory = risks?.reduce((acc, r) => {
    const cat = r?.category || 'Other';
    acc[cat] = (acc?.[cat] || 0) + 1;
    return acc;
  }, {});

  const riskCategoryData = Object.entries(riskByCategory)?.map(([name, count]) => ({ name, count }));

  // CAPA breakdown by status
  const capaByStatus = capaActions?.reduce((acc, c) => {
    const s = c?.capa_status || 'open';
    acc[s] = (acc?.[s] || 0) + 1;
    return acc;
  }, {});

  // Partners with most risks
  const partnerRiskCount = risks?.reduce((acc, r) => {
    if (r?.partner_id) acc[r?.partner_id] = (acc?.[r?.partner_id] || 0) + 1;
    return acc;
  }, {});

  const topRiskyPartners = Object.entries(partnerRiskCount)
    ?.sort((a, b) => b?.[1] - a?.[1])
    ?.slice(0, 5)
    ?.map(([pid, count]) => {
      const p = partners?.find(p => p?.id === pid);
      return { name: p?.legal_name || p?.brand_name || 'Unknown', count, id: pid };
    });

  const revenueData = metricsHistory?.map(m => ({
    month: new Date(m?.metric_month)?.toLocaleString('default', { month: 'short' }),
    revenue: m?.revenue,
    target: m?.target_revenue
  }));

  const performanceData = metricsHistory?.map(m => ({
    month: new Date(m?.metric_month)?.toLocaleString('default', { month: 'short' }),
    winRate: m?.win_rate,
    slaCompliance: m?.sla_compliance,
    deliveryQuality: m?.delivery_quality
  }));

  const riskPredictions = [
    {
      id: 1,
      title: "Projected SLA Compliance Drop",
      description: "AI analysis predicts potential SLA compliance decrease to 89% in next quarter based on current resource allocation and project pipeline.",
      severity: "high",
      probability: 72,
      impact: "Revenue impact of OMR 150K due to penalty clauses and potential tier downgrade affecting future opportunities.",
      recommendation: "Increase technical support staffing by 2 FTEs and implement proactive monitoring system for early issue detection.",
      actionType: "immediate",
      actionLabel: "Review Staffing Plan"
    },
    {
      id: 2,
      title: "Client Churn Risk - TechCorp Account",
      description: "Engagement metrics show 40% decrease in interaction frequency and delayed response to renewal discussions.",
      severity: "critical",
      probability: 65,
      impact: "Loss of OMR 800K annual revenue and strategic reference account in financial services sector.",
      recommendation: "Schedule executive-level review meeting within 7 days and prepare customized retention proposal with enhanced service offerings.",
      actionType: "escalate",
      actionLabel: "Escalate to Leadership"
    },
    {
      id: 3,
      title: "Certification Gap Impact",
      description: "Current certification renewal timeline creates 3-week gap where partner will not meet Gold tier requirements.",
      severity: "medium",
      probability: 85,
      impact: "Temporary tier downgrade affecting deal registration priority and co-marketing fund eligibility.",
      recommendation: "Expedite certification renewal process and request temporary waiver based on historical performance.",
      actionType: "scheduled",
      actionLabel: "Schedule Review"
    }
  ];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'risks', label: 'All Risks', icon: 'Shield' },
    { id: 'actions', label: 'All CAPAs', icon: 'ListChecks' },
    { id: 'predictions', label: 'AI Predictions', icon: 'Brain' }
  ];

  const getSeverityColor = (severity) => {
    const colors = { critical: 'bg-red-100 text-red-700', high: 'bg-orange-100 text-orange-700', medium: 'bg-yellow-100 text-yellow-700', low: 'bg-green-100 text-green-700' };
    return colors?.[severity] || 'bg-slate-100 text-slate-600';
  };

  return (
    <>
      <Helmet>
        <title>Governance Rollup Dashboard - PartnerHub</title>
        <meta name="description" content="Global governance rollup — aggregated risk, CAPA, and performance view across all partners" />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-purple-50/20">
        <Header />

        <main className="pt-[148px] pb-32 px-24 lg:px-32">
          <div className="max-w-[1600px] mx-auto">

            {/* Page Header */}
            <div className="mb-32">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-20 mb-24">
                <div>
                  <div className="flex items-center gap-12 mb-12">
                    <div className="p-12 bg-gradient-to-br from-primary to-secondary rounded-xl shadow-lg">
                      <Icon name="Shield" size={28} className="text-white" />
                    </div>
                    <div>
                      <h1 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
                        Governance Rollup
                      </h1>
                      <p className="text-slate-600 mt-4">Aggregated risk &amp; performance view across all partners</p>
                    </div>
                  </div>

                  {/* Info banner */}
                  <div className="flex items-start gap-12 px-20 py-14 bg-blue-50 border border-blue-200 rounded-xl text-blue-700 text-sm max-w-2xl">
                    <Icon name="Info" size={16} className="mt-1 flex-shrink-0" />
                    <span>
                      This is the global rollup view. For partner-specific governance, open a{' '}
                      <button
                        onClick={() => navigate('/partner-profile-portal')}
                        className="font-semibold underline hover:text-blue-900 transition-colors"
                      >
                        Partner Profile
                      </button>
                      {' '}and go to the <strong>Governance</strong> tab.
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-10 flex-wrap">
                  <button
                    onClick={() => navigate('/partner-profile-portal')}
                    className="flex items-center gap-8 px-20 py-11 rounded-xl bg-gradient-to-r from-primary to-secondary text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all"
                  >
                    <Icon name="User" size={16} />
                    View Partner Governance
                  </button>
                  <button
                    onClick={loadData}
                    className="flex items-center gap-8 px-20 py-11 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors"
                  >
                    <Icon name="RefreshCw" size={16} />
                    Refresh
                  </button>
                </div>
              </div>

              {/* Tabs */}
              <div className="flex gap-8 overflow-x-auto pb-4 scrollbar-hide">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`
                      flex items-center gap-10 px-24 py-14 rounded-xl font-semibold text-sm whitespace-nowrap transition-all
                      ${activeTab === tab?.id
                        ? 'bg-gradient-to-r from-primary to-secondary text-white shadow-lg shadow-primary/25'
                        : 'bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900 border border-slate-200'}
                    `}
                  >
                    <Icon name={tab?.icon} size={18} />
                    {tab?.label}
                  </button>
                ))}
              </div>
            </div>

            {loading && (
              <div className="flex items-center justify-center py-64">
                <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
              </div>
            )}

            {error && !loading && (
              <div className="flex items-center gap-12 p-20 bg-red-50 border border-red-200 rounded-xl mb-24 text-red-700 text-sm">
                <Icon name="AlertCircle" size={18} />
                <span>Error: {error}</span>
                <button onClick={loadData} className="ml-auto font-medium underline">Retry</button>
              </div>
            )}

            {/* Overview Tab */}
            {!loading && activeTab === 'overview' && (
              <div className="space-y-24">
                {/* Rollup KPI Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-16">
                  {rollupMetrics?.map((metric, index) => (
                    <PerformanceMetricCard key={index} {...metric} />
                  ))}
                </div>

                {/* Charts Row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-24">
                  <div className="bg-white rounded-2xl shadow-lg border border-slate-200/60 p-24">
                    <h3 className="text-lg font-bold text-slate-900 mb-20">Revenue vs Target (All Partners)</h3>
                    {revenueData?.length > 0 ? (
                      <PerformanceTrendChart
                        title="Revenue Performance"
                        data={revenueData}
                        dataKeys={[{ key: 'revenue', color: '#3B82F6', name: 'Revenue' }, { key: 'target', color: '#8B5CF6', name: 'Target' }]}
                        xAxisKey="month"
                        yAxisLabel="Amount (OMR)"
                      />
                    ) : (
                      <div className="flex items-center justify-center h-48 text-slate-400 text-sm">No metrics data available</div>
                    )}
                  </div>

                  <div className="bg-white rounded-2xl shadow-lg border border-slate-200/60 p-24">
                    <h3 className="text-lg font-bold text-slate-900 mb-20">Performance Metrics Trend</h3>
                    {performanceData?.length > 0 ? (
                      <PerformanceTrendChart
                        title="Performance Metrics Trend"
                        data={performanceData}
                        dataKeys={[
                          { key: 'winRate', color: '#10B981', name: 'Win Rate (%)' },
                          { key: 'slaCompliance', color: '#3B82F6', name: 'SLA Compliance (%)' },
                          { key: 'deliveryQuality', color: '#F59E0B', name: 'Quality Score' }
                        ]}
                        xAxisKey="month"
                        yAxisLabel="Score"
                      />
                    ) : (
                      <div className="flex items-center justify-center h-48 text-slate-400 text-sm">No metrics data available</div>
                    )}
                  </div>
                </div>

                {/* Risk Summary + Top Risky Partners */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-24">
                  {/* Risk by Category */}
                  <div className="bg-white rounded-2xl shadow-lg border border-slate-200/60 p-24">
                    <h3 className="text-lg font-bold text-slate-900 mb-20">Risks by Category</h3>
                    {riskCategoryData?.length > 0 ? (
                      <div className="space-y-12">
                        {riskCategoryData?.map((item) => (
                          <div key={item?.name} className="flex items-center gap-12">
                            <span className="text-sm text-slate-600 w-28 truncate">{item?.name}</span>
                            <div className="flex-1 bg-slate-100 rounded-full h-8">
                              <div
                                className="h-8 rounded-full bg-gradient-to-r from-primary to-secondary"
                                style={{ width: `${Math.min(100, (item?.count / Math.max(...riskCategoryData?.map(r => r?.count))) * 100)}%` }}
                              />
                            </div>
                            <span className="text-sm font-semibold text-slate-900 w-6 text-right">{item?.count}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-32 text-slate-400 text-sm">No risks logged</div>
                    )}
                  </div>

                  {/* Partners with Most Risks */}
                  <div className="bg-white rounded-2xl shadow-lg border border-slate-200/60 p-24">
                    <div className="flex items-center justify-between mb-20">
                      <h3 className="text-lg font-bold text-slate-900">Partners with Open Risks</h3>
                      <button
                        onClick={() => navigate('/partner-profile-portal')}
                        className="text-xs text-primary font-semibold hover:underline flex items-center gap-4"
                      >
                        View Profiles <Icon name="ArrowRight" size={12} />
                      </button>
                    </div>
                    {topRiskyPartners?.length > 0 ? (
                      <div className="space-y-12">
                        {topRiskyPartners?.map((p) => (
                          <div key={p?.id} className="flex items-center gap-12">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                              {p?.name?.[0]?.toUpperCase() || '?'}
                            </div>
                            <span className="flex-1 text-sm font-medium text-slate-800 truncate">{p?.name}</span>
                            <span className="px-10 py-4 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                              {p?.count} risk{p?.count !== 1 ? 's' : ''}
                            </span>
                            <button
                              onClick={() => navigate(`/partner-profile-portal/${p?.id}`)}
                              className="p-6 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-primary transition-colors"
                            >
                              <Icon name="ArrowRight" size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-32 text-slate-400 text-sm">No partner risks logged</div>
                    )}
                  </div>
                </div>

                {/* CAPA Status Summary */}
                <div className="bg-white rounded-2xl shadow-lg border border-slate-200/60 p-24">
                  <h3 className="text-lg font-bold text-slate-900 mb-20">CAPA Status Summary (All Partners)</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-16">
                    {Object.entries(capaByStatus)?.map(([status, count]) => (
                      <div key={status} className="flex flex-col items-center p-16 bg-slate-50 rounded-xl border border-slate-200">
                        <span className="text-2xl font-bold text-slate-900">{count}</span>
                        <span className="text-xs font-semibold text-slate-500 mt-4 capitalize">{status?.replace(/_/g, ' ')}</span>
                      </div>
                    ))}
                    {Object.keys(capaByStatus)?.length === 0 && (
                      <div className="col-span-4 flex items-center justify-center h-20 text-slate-400 text-sm">No CAPA actions logged</div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* All Risks Tab */}
            {!loading && activeTab === 'risks' && (
              <div className="space-y-24">
                <div className="bg-white rounded-2xl shadow-lg border border-slate-200/60 p-24">
                  <RiskMatrixPanel risks={risks} onUpdateRisk={() => {}} />
                </div>
                <div className="bg-white rounded-2xl shadow-lg border border-slate-200/60 p-24">
                  <RiskPredictionPanel predictions={riskPredictions} />
                </div>
              </div>
            )}

            {/* All CAPAs Tab */}
            {!loading && activeTab === 'actions' && (
              <div className="bg-white rounded-2xl shadow-lg border border-slate-200/60 p-24">
                <CAPAActionsTable
                  actions={capaActions}
                  onUpdateAction={() => {}}
                  onUpdateStatus={() => {}}
                  onViewDetails={() => {}}
                />
              </div>
            )}

            {/* AI Predictions Tab */}
            {!loading && activeTab === 'predictions' && (
              <div className="bg-white rounded-2xl shadow-lg border border-slate-200/60 p-24">
                <RiskPredictionPanel predictions={riskPredictions} />
              </div>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default GovernancePerformanceDashboard;